/**
 * @file api.ts
 * API wrapper to communicate with the Cloudflare Worker backend.
 */

import { URL_KEY, buildAppUrl, buildLoginUrl, readApp, readProgramId, type AppName } from '../shared/utils/appUrl';

declare global {
  interface Window {
    GRM_CONFIG?: {
      dbId: string;
      app?: AppName;
      apiUrl?: string; // Optional URL for Cloudflare API
    };
    // Cloudflare Turnstile token for the next protected call; apiCall sends it as x-turnstile-token.
    // Tokens are single-use, so whoever spends one clears it.
    GRM_BOT_TOKEN?: string;
  }
}

/**
 * Resolves the API base URL and the caller's session token for the current portal context
 * (admin / ops / household-apply). Shared by `apiCall` (JSON RPC) and
 * `fetchApplicationDocument` (raw authenticated GET) so both stay in sync on token precedence.
 */
function resolveApiContext(): { apiUrl: string | undefined; token: string | null; dbId: string | null; appParam: AppName | null } {
  const urlParams = new URLSearchParams(window.location.search);
  const dbId = window.GRM_CONFIG?.dbId || readProgramId(urlParams);
  const appParam = window.GRM_CONFIG?.app || readApp(urlParams);
  let apiUrl = window.GRM_CONFIG?.apiUrl || (import.meta.env.VITE_CF_API_URL);

  // Fallback for local development
  if (!apiUrl && window.location.hostname === 'localhost') {
    apiUrl = 'http://localhost:8787';
  }

  // 1. Determine Token (Check both stores)
  const vToken = localStorage.getItem('volunteer_session_token') || sessionStorage.getItem('volunteer_session_token');
  const hToken = localStorage.getItem('household_token');
  const aToken = sessionStorage.getItem('session_token');

  let token = aToken;
  if (appParam === 'admin') {
    // ADR-129: the Admin Portal and the Ops Hub keep separate sessions — no cross-fallback.
    token = aToken;
  } else if (appParam === 'ops') {
    token = vToken;
  } else if (appParam === 'household' || appParam === 'apply') {
    token = hToken || aToken;
  } else {
    token = vToken || aToken || hToken;
  }

  return { apiUrl, token, dbId, appParam: appParam || null };
}

/**
 * Fetches an uploaded application document's raw bytes via the Admin/Coordinator-gated
 * `GET /artifact/:fileId` route (`src/api/src/artifact.ts`) — the browser never talks to R2
 * directly. Used for the review-mode "View"/"Delete" document actions (`DynamicFormField.tsx`);
 * a plain `<a href>` can't carry the Bearer token this route requires.
 */
export async function fetchApplicationDocument(fileId: string): Promise<Blob> {
  const { apiUrl, token } = resolveApiContext();
  if (!apiUrl) throw new Error('API URL is not configured.');
  if (!token) throw new Error('Authentication required.');

  // fileId is a real multi-segment R2 key ({programId}/{campaignId}/{purpose}/{uuid}-{fileName},
  // see R2Service.uploadFile) — encode each segment individually so the "/"s survive as real path
  // separators; encoding the whole string turns them into %2F, which the server's programId-prefix
  // check (artifact.ts) then reads as one opaque segment and always rejects with 403.
  const encodedPath = fileId.split('/').map(encodeURIComponent).join('/');
  const response = await fetch(`${apiUrl}/artifact/${encodedPath}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error(response.status === 404 ? 'Document not found.' : 'Failed to load document.');
  }
  return response.blob();
}

/**
 * Executes a server-side function via the Cloudflare Worker API.
 * Automatically injects context and routes to the backend.
 */
export async function apiCall<T>(methodName: string, ...args: unknown[]): Promise<T> {
  const urlParams = new URLSearchParams(window.location.search);
  const { apiUrl, token, dbId, appParam } = resolveApiContext();

  // Bot protection token (Turnstile)
  const botToken = window.GRM_BOT_TOKEN;

  // DEMO MODE BYPASS: Intercept specific calls to provide mock data for UI testing
  if (urlParams.get('demo') === 'true') {
    if (methodName === 'getSessionInfo') {
      return {
        userId: 'M1001',
        email: 'demo@example.com',
        firstName: 'Demo',
        role: 'Household',
        expires: Date.now() + 3600000,
        authMethod: 'Magic',
        dbId: dbId || ''
      } as T;
    }
    if (methodName === 'getSystemConfig') {
      return {
        ORGANIZATION_NAME: 'Community Gift Program',
        ONLINE_APPLICATIONS_ENABLED: true,
        INTAKE_APPOINTMENTS_ENABLED: true,
        ACTIVE_CAMPAIGN_ID: 'C2026',
        ORG_TIMEZONE: 'America/Chicago'
      } as T;
    }
    if (methodName === 'getOrganizationName') {
      return 'Community Gift Program' as unknown as T;
    }
    if (methodName === 'getAvailableEvents') {
      return [
        { 
          Event_ID: 'E1', 
          Name: 'Registration Review', 
          Date: new Date().toISOString().split('T')[0], 
          Booking_Open: true,
          Event_Type: 'REVIEW'
        },
        { 
          Event_ID: 'E2', 
          Name: 'Gift Pickup', 
          Date: new Date(Date.now() + 86400000 * 7).toISOString().split('T')[0], 
          Booking_Open: true,
          Event_Type: 'DISTRIBUTION'
        }
      ] as T;
    }
    if (methodName === 'getParticipantBookings') {
      return [] as T;
    }
    if (methodName === 'getAvailableSlots') {
      const now = new Date();
      now.setMinutes(0, 0, 0);
      return Array.from({ length: 12 }, (_, i) => {
        const startTime = new Date(now.getTime() + (i + 17) * 1800000);
        const endTime = new Date(startTime.getTime() + 1800000);
        return {
          Slot_ID: `S${i}`,
          Event_ID: args[0] as string,
          Start_Time: startTime.toISOString(),
          End_Time: endTime.toISOString(),
          Status: 'OPEN',
          Category: 'HOUSEHOLD'
        };
      }) as T;
    }
  }

  if (!apiUrl) {
    throw new Error('API URL is not configured.');
  }

  return callBackend<T>(apiUrl, methodName, token, dbId, args, botToken, appParam || '');
}

/**
 * Executes a call to the Backend API.
 */
async function callBackend<T>(apiUrl: string, methodName: string, token: string | null, dbId: string | null, args: unknown[], botToken?: string, app?: AppName | null): Promise<T> {
  const url = `${apiUrl}/api/${methodName}${dbId ? `?${URL_KEY.program}=${encodeURIComponent(dbId)}` : ''}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (botToken) {
    headers['x-turnstile-token'] = botToken;
  }

  // Always use POST for the API to support arbitrary arguments
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ args }),
  });

  if (!response.ok) {
    let errorMsg = 'API Request failed';
    try {
      const error = await response.json() as Record<string, unknown>;
      errorMsg = error.message || errorMsg;
    } catch {
      errorMsg = await response.text() || errorMsg;
    }

    if (response.status === 401 && methodName !== 'provisionNewProgram') {
      // ADR-129: clear only the expired scope's token — the other portal's session is independent.
      if (app === 'ops') {
        sessionStorage.removeItem('volunteer_session_token');
        localStorage.removeItem('volunteer_session_token');
      } else if (app === 'household') {
        localStorage.removeItem('household_token');
      } else {
        sessionStorage.removeItem('session_token');
      }
      if (app === 'ops' || app === 'household') {
        // DL-546: both portals own their sign-in screen (Ops: volunteer gateway, Household: emailed code),
        // so an expired session returns there — /auth/login is the Admin SSO page and can't sign them in.
        window.location.href = buildAppUrl('', { app, programId: dbId }, { expired: 'true' });
      } else {
        window.location.href = buildLoginUrl('', { app: app ?? 'admin', programId: dbId });
      }
    }
    throw new Error(errorMsg);
  }

  const text = await response.text();
  if (!text) {
    return undefined as unknown as T;
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    return text as unknown as T;
  }
}

/**
 * Logs the user out by clearing local tokens and optionally invalidating the session on the server.
 */
export async function logout(): Promise<void> {
  const urlParams = new URLSearchParams(window.location.search);
  const dbId = window.GRM_CONFIG?.dbId || readProgramId(urlParams);
  
  try {
    // Attempt to invalidate session on backend if we have a token
    await apiCall('logout');
  } catch {
    if (import.meta.env.DEV) console.warn('[AUTH] Backend logout failed, clearing local session anyway.');
  } finally {
    sessionStorage.removeItem('session_token');
    // Review environment only reads this (see useAdminState): keep the login screen reachable in this tab after Sign Out (sessionStorage, DL-545).
    try { sessionStorage.setItem('review_autologin_off', '1'); } catch { /* ignore */ }
    const redirectUrl = dbId ? buildLoginUrl('', { app: 'admin', programId: dbId }) : '/auth/login';
    window.location.href = redirectUrl;
  }
}
